import React, { useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from "@mui/material";
import TimesheetEditor from "../../components/timesheets/TimesheetEditor";
import { Timesheet } from "../../pages/types/timesheet";
import { listTimesheets, upsertTimesheet, calcTotalHours } from "../../pages/services/timesheetsApi";
import { useAuthState } from "../../context/auth-context";

export default function ManageTimesheets() {
    const { user } = useAuthState()as any;
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState<Timesheet | null>(null);

    const all = useMemo(() => {
        const items = listTimesheets();
        if (!q.trim()) return items;
        const s = q.toLowerCase();
        return items.filter((t) => `${t.employeeName} ${t.weekStart} ${t.status}`.toLowerCase().includes(s));
    }, [q, open, active]);

    if (user?.role !== "admin" && user?.role !== "manager") {
        return <Typography>You don’t have access.</Typography>;
    }

    const statusColor = (s: string) => {
        if (s === "SUBMITTED") return "info";
        if (s === "APPROVED") return "success";
        if (s === "REJECTED") return "error";
        return "default";
    };

    return (
        <Stack spacing={2}>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>Manage Timesheets</Typography>

            <Card>
                <CardContent>
                    <TextField label="Search (name / week / status)" value={q} onChange={(e) => setQ(e.target.value)} fullWidth />
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>All Timesheets</Typography>
                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1.25}>
                        {all.length === 0 && <Typography sx={{ opacity: 0.75 }}>No records.</Typography>}
                        {all.map((t) => (
                            <Card key={t.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                        <Typography sx={{ fontWeight: 800 }}>{t.employeeName} • {t.weekStart}</Typography>
                                        <Chip size="small" label={t.status} color={statusColor(t.status) as any} />
                                        <Typography sx={{ opacity: 0.75 }}>Hours: {calcTotalHours(t)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Button variant="outlined" onClick={() => { setActive(t); setOpen(true); }}>
                                            Review
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontWeight: 900 }}>
                    Review • {active?.employeeName} • {active?.weekStart}
                </DialogTitle>
                <DialogContent dividers>
                    {active && <TimesheetEditor value={active} onChange={setActive} readOnly={false} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                    <Button color="error" variant="outlined" onClick={() => active && setActive(upsertTimesheet({ ...active, status: "REJECTED" }))}>
                        Reject
                    </Button>
                    <Button color="success" variant="contained" onClick={() => active && setActive(upsertTimesheet({ ...active, status: "APPROVED" }))}>
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}